import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddConfigureFeature from "./AddConfigureFeature";
import SortFeaturesOffcanvas from "./SortingFeature";

/* If TS complains about react-beautiful-dnd types, create src/types/react-beautiful-dnd.d.ts:
   declare module "react-beautiful-dnd";
*/

interface Feature {
  _id: string;
  key: string;
  title: string;
  description?: string | null;
  dataType?: string;
  meta?: object;
  sortOrder?: number;
  isActive?: boolean;
  sectionId?: string; // we'll keep this for convenience
}

interface SectionItem {
  sectionId: {
    _id: string;
    title: string;
  };
  features: Feature[];
  sortOrder?: number;
}

const ConfigureFeature: React.FC = () => {
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState<string | null>(null);
  const [showAddFeature, setShowAddFeature] = useState(false);
  const [featureToEdit, setFeatureToEdit] = useState<Feature | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const [sortSection, setSortSection] = useState<SectionItem | null>(null);
  const loadSections = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: "memberShipBenifits/section-wise-list",
      });
      const perm = res?.permissions || {};

      setPagePermission({
        canView: perm.canView,
        canAdd: perm.canAdd,
        canEdit: perm.canEdit,
        canDelete: perm.canDelete,
      });
      // expected res.data = array of { sectionId: {_id, title}, features: [...] }
      const data: SectionItem[] = res?.data || [];
      setSections(data);
      // default open first section
      if (data.length && Object.keys(openSections).length === 0) {
        setOpenSections({ [data[0].sectionId._id]: true });
      }
    } catch (err) {
      console.error("Failed to load sections:", err);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  const toggleSection = (id: string) => {
    setOpenSections((p) => ({ ...p, [id]: !p[id] }));
  };

  const handleDeleteFeature = (id: string) => {
    setFeatureToDelete(id);
    setShowDeleteModal(true);
  };

  return (
    <>
      <AddConfigureFeature
        show={showAddFeature}
        handleClose={() => {
          setShowAddFeature(false);
          setFeatureToEdit(null);
          loadSections();
        }}
        data={featureToEdit}
      />

      <SortFeaturesOffcanvas
        show={!!sortSection}
        section={sortSection}
        onHide={() => setSortSection(null)}
        onSaved={loadSections}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (!featureToDelete) return;
          WebService.deleteAPI({
            action: `memberShipBenifits/delete/${featureToDelete}`,
          }).then(() => {
            setShowDeleteModal(false);
            loadSections();
          }).catch(err => {
            console.error("Delete error:", err);
            setShowDeleteModal(false);
          });
        }}
        message="Are you sure you want to delete this feature?"
      />

      <div className="events-main">
        <Card className="event-card p-3">
          <div className="d-flex justify-content-between mb-3">
            <div className="page-title">Configure Benefits</div>
            {pagePermission.canAdd && (
              <CustomButton
                label={savingOrder ? "Saving..." : "Add"}
                isLoading={false}
                onClick={() => setShowAddFeature(true)}
                style={{ width: 100 }}
              />)}
          </div>

          {/* <DragDropContext onDragEnd={onDragEnd}> */}
          {sections.map((section) => (
            <div
              key={section.sectionId._id}
              className="border rounded mb-3 shadow-sm"
            >
              {/* SECTION HEADER (NOT DRAGGABLE) */}
              <div className="px-4 py-3 bg-blue-100 d-flex justify-content-between align-items-center">
                <span className="fw-bold text-primary">
                  {section.sectionId.title}
                </span>

                <div className="d-flex align-items-center gap-3">
                  {/* SORT BUTTON */}
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => {
                      e.stopPropagation();   // 🔥 important
                      setSortSection(section);
                    }}
                  >
                    Sort
                  </button>

                  {/* TOGGLE ICON */}
                  <span
                    style={{ fontSize: 18, cursor: "pointer" }}
                    onClick={() => toggleSection(section.sectionId._id)}
                  >
                    {openSections[section.sectionId._id] ? "▲" : "▼"}
                  </span>
                </div>
              </div>

              {/* FEATURES */}
              {openSections[section.sectionId._id] &&
                section.features.map((feature) => (
                  <div
                    key={feature._id}
                    className="px-4 py-3 border-bottom d-flex justify-content-between"
                  >
                    <div>
                      <div>{feature.title}</div>
                      {feature.description && (
                        <small className="text-muted fst-italic">
                          {feature.description}
                        </small>
                      )}
                    </div>

                    <div>
                      {pagePermission.canEdit && (
                        <PencilFill
                          className="me-3 cursor-pointer"
                          onClick={() => {
                            setFeatureToEdit(feature);
                            setShowAddFeature(true);
                          }}
                        />
                      )}
                      {pagePermission.canDelete && (
                        <TrashFill
                          color="#DC143C"
                          className="cursor-pointer"
                          onClick={() => handleDeleteFeature(feature._id)}
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ))}
          {/* </DragDropContext> */}
        </Card>
      </div>
    </>
  );
};

export default ConfigureFeature;
